import { DateTime } from 'luxon';
import type { DraftRetainerPreviewParams } from '../types/retainerPreview';
import {
    expandFirstClassLegalDocTags,
    getExtraLegalDocTags,
    normalizeLegalDocPrefills,
} from './legalDocPrefills';

export const LEGAL_DOC_DATE_FORMAT = 'MM-dd-yyyy';

export const SIGN_TIME_PLACEHOLDERS = new Set(['client_signature', 'date']);

export const DRAFT_RETAINER_PREVIEW_PARAM_KEYS = [
    'customFileNumber',
    'lawyerName',
    'lawyerSignature',
    'lawyerEmail',
    'firstCcUserName',
    'firstCcUserEmail',
    'practiceArea',
    'matterType',
    'createdAt',
    'clientName',
    'clientFirstName',
    'clientLastName',
    'allClientNames',
    'clientEmail',
    'retainerAmount',
    'subjectAddress',
    'closingDate',
    'purchasePrice',
] as const satisfies readonly (keyof Omit<DraftRetainerPreviewParams, 'legalDocPrefills'>)[];

const PLACEHOLDER_TO_PREVIEW_PARAM_KEYS: Record<
    string,
    (typeof DRAFT_RETAINER_PREVIEW_PARAM_KEYS)[number][]
> = {
    custom_file_number: ['customFileNumber'],
    lawyer_name: ['lawyerName'],
    lawyer_signature: ['lawyerSignature'],
    lawyer_email: ['lawyerEmail'],
    cc_user_name: ['firstCcUserName'],
    cc_user_email: ['firstCcUserEmail'],
    assigned_user: ['lawyerName'],
    assigned_user_email: ['lawyerEmail'],
    prospect_name: [],
    practice_area: ['practiceArea'],
    matter_type: ['matterType'],
    prospect_created_datetime: ['createdAt'],
    prospect_created_date: ['createdAt'],
    created_time: ['createdAt'],
    client_name: ['clientName'],
    client_first_name: ['clientFirstName'],
    client_last_name: ['clientLastName'],
    all_client_names: ['allClientNames'],
    client_email: ['clientEmail'],
    ...expandFirstClassLegalDocTags<(typeof DRAFT_RETAINER_PREVIEW_PARAM_KEYS)[number][]>({
        retainer_amount: ['retainerAmount'],
        subject_address: ['subjectAddress'],
        closing_date: ['closingDate'],
        purchase_price: ['purchasePrice'],
    }),
};

export function parseRetainerDocumentDate(value: string | undefined): string | undefined {
    if (!value?.trim()) return undefined;

    const parsed = DateTime.fromFormat(value.trim(), LEGAL_DOC_DATE_FORMAT);
    return parsed.isValid ? parsed.toFormat(LEGAL_DOC_DATE_FORMAT) : undefined;
}

export function hasDraftRetainerPreviewParams(params: DraftRetainerPreviewParams): boolean {
    const hasStringParams = DRAFT_RETAINER_PREVIEW_PARAM_KEYS.some((key) => {
        const value = params[key];
        return value !== undefined && value !== '';
    });
    if (hasStringParams) {
        return true;
    }

    return Object.values(params.legalDocPrefills ?? {}).some((value) => value !== '');
}

/** Keeps only preview params that correspond to placeholders present on the template. */
export function filterDraftRetainerPreviewByPlaceholders(
    params: DraftRetainerPreviewParams,
    placeholders: string[] | null | undefined,
): DraftRetainerPreviewParams {
    if (!placeholders?.length) {
        return {};
    }

    const allowedKeys = new Set<(typeof DRAFT_RETAINER_PREVIEW_PARAM_KEYS)[number]>();
    for (const placeholder of placeholders) {
        const paramKeys = PLACEHOLDER_TO_PREVIEW_PARAM_KEYS[placeholder];
        if (paramKeys) {
            for (const key of paramKeys) {
                allowedKeys.add(key);
            }
        }
    }

    const filtered: DraftRetainerPreviewParams = {};
    for (const key of DRAFT_RETAINER_PREVIEW_PARAM_KEYS) {
        const value = params[key];
        if (allowedKeys.has(key) && value !== undefined && value !== '') {
            filtered[key] = value;
        }
    }

    const extraTags = getExtraLegalDocTags(placeholders);
    if (extraTags.length > 0 && params.legalDocPrefills) {
        const extraPrefills: Record<string, string> = {};
        for (const tag of extraTags) {
            const value = params.legalDocPrefills[tag];
            if (value) {
                extraPrefills[tag] = value;
            }
        }
        if (Object.keys(extraPrefills).length > 0) {
            filtered.legalDocPrefills = extraPrefills;
        }
    }

    return filtered;
}

export function normalizeDraftRetainerPreviewParams(
    params: DraftRetainerPreviewParams,
): DraftRetainerPreviewParams {
    const normalized: DraftRetainerPreviewParams = {};

    for (const key of DRAFT_RETAINER_PREVIEW_PARAM_KEYS) {
        const value = params[key]?.trim();
        if (value) {
            normalized[key] = value;
        }
    }

    if (normalized.retainerAmount && Number.isNaN(Number.parseFloat(normalized.retainerAmount))) {
        delete normalized.retainerAmount;
    }

    if (normalized.purchasePrice && Number.isNaN(Number.parseFloat(normalized.purchasePrice))) {
        delete normalized.purchasePrice;
    }

    if (normalized.closingDate && !/^\d{4}-\d{2}-\d{2}$/.test(normalized.closingDate)) {
        delete normalized.closingDate;
    }

    if (normalized.createdAt) {
        const parsedCreatedAt = parseRetainerDocumentDate(normalized.createdAt);
        if (parsedCreatedAt) {
            normalized.createdAt = parsedCreatedAt;
        } else {
            delete normalized.createdAt;
        }
    }

    const legalDocPrefills = normalizeLegalDocPrefills(params.legalDocPrefills);
    if (legalDocPrefills) {
        normalized.legalDocPrefills = legalDocPrefills;
    }

    return normalized;
}

/** Placeholders filled at preview time (excludes signature and date). */
export function getPreviewPlaceholders(placeholders: string[] | null | undefined): string[] {
    if (!placeholders?.length) {
        return [];
    }

    return placeholders.filter((placeholder) => !SIGN_TIME_PLACEHOLDERS.has(placeholder));
}

export function hasPreviewFillContent(
    fieldValues: Record<string, string>,
    docxProspectJson: Record<string, string | Record<string, string>>,
): boolean {
    return (
        Object.values(fieldValues).some((value) => value !== '') ||
        Object.entries(docxProspectJson).some(([key, value]) => {
            if (key === 'FieldValues' && typeof value === 'object') {
                return Object.values(value).some((fieldValue) => fieldValue !== '');
            }
            return value !== '';
        })
    );
}
